import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import OpenWeatherIcon from '~/client/src/shared/components/OpenWeatherIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { temperatureUnits } from '~/client/src/shared/enums/WeatherUnits'
import WeatherForecastsStore from '~/client/src/shared/stores/domain/WeatherForecasts.store'

import DetailedWeatherWidget from './DetailedWeatherWidget'

interface IProps {
  className: string
  weatherForecastsStore?: WeatherForecastsStore
  onMount: () => void
}

@inject('weatherForecastsStore')
@observer
export default class CompactWeather extends React.Component<IProps> {
  private isLoading: boolean = true
  @observable private isOpen: boolean = false

  @action.bound
  public toggleOpen(value?: boolean | undefined) {
    this.isOpen = value !== undefined ? value : !this.isOpen
  }

  public constructor(props: IProps) {
    super(props)

    makeObservable(this)
  }

  public componentDidMount() {
    // Fetch the weekly forecast when the component mounts
    const todayAtNoon = new Date()
    todayAtNoon.setHours(12, 0, 0, 0)
    this.props.weatherForecastsStore.fetch5DayForecast(todayAtNoon.getTime())
  }

  public componentDidUpdate(): void {
    // Call onMount when loading is finished
    if (!this.props.weatherForecastsStore.isLoading && this.isLoading) {
      this.props.onMount?.()
      this.isLoading = false
    }
  }

  private renderWeatherDisplay(
    icon: React.ReactNode,
    temperature: string,
    isInteractive: boolean,
  ) {
    return (
      <div
        className={classList({
          'compact-weather tw:flex tw:items-center': true,
          pointer: isInteractive,
        })}
      >
        <div className="col pr4 text white as-stretch y-end">{icon}</div>
        <div className="col text large white">{temperature}</div>
        <ChevronDownIcon
          size={16}
          className={classList({
            'ml7 vertical-align-middle tw:text-zinc-200': true,
            rotate180: isInteractive && this.isOpen,
          })}
        />
      </div>
    )
  }

  public render() {
    const { weatherForecastsStore, className } = this.props
    const { todayForecast, weatherUnits } = weatherForecastsStore
    const currentTemperature = todayForecast?.currentTemp
    const temperatureUnit = temperatureUnits(weatherUnits)

    if (!todayForecast) {
      if (!weatherForecastsStore.isLoading) {
        return null
      }

      return (
        <div className={className}>
          {this.renderWeatherDisplay(
            <div style={{ width: 24 }} />,
            `--${temperatureUnit}`,
            false,
          )}
        </div>
      )
    }

    return (
      <div className={className}>
        <Popover open={this.isOpen} onOpenChange={this.toggleOpen}>
          <PopoverTrigger
            nativeButton={false}
            render={this.renderWeatherDisplay(
              <OpenWeatherIcon
                openWeatherIconCode={todayForecast?.weatherIconCode}
              />,
              currentTemperature != null
                ? `${currentTemperature}${temperatureUnit}`
                : '',
              true,
            )}
          />
          <PopoverContent
            side="bottom"
            align="start"
            sideOffset={10}
            alignOffset={-10}
            className="tw:w-auto tw:max-w-(--available-width) tw:overflow-x-auto"
          >
            <DetailedWeatherWidget
              weatherForecastsStore={weatherForecastsStore}
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }
}
